import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

// Extend dayjs with plugins
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

// Track if locale has been set up
let localeSetup = false;

/**
 * Sets up the dayjs locale based on the user's browser language
 */
export async function setupLocale() {
	if (localeSetup) return;

	const userLocale = navigator.language.split('-')[0]; // e.g., 'en' from 'en-US'
	try {
		// Dynamically import the locale
		await import(`dayjs/locale/${userLocale}.js`);
		dayjs.locale(userLocale);
		localeSetup = true;
	} catch {
		// Fallback to English if locale not available
		dayjs.locale('en');
		localeSetup = true;
	}
}

// Initialize locale setup
setupLocale();

/**
 * Formats a timestamp to a localized time string
 * @param timestamp - Date string or Date object
 * @param includeDate - Whether to include the date (default: false)
 * @returns Formatted time string like "3:30:45 PM" or "Nov 28, 2024 3:30:45 PM"
 */
export function formatTimestamp(timestamp: string | Date, includeDate = false): string {
	if (includeDate) {
		return dayjs(timestamp).format('lll'); // Localized with date and time
	}
	return dayjs(timestamp).format('LTS'); // Localized time only
}

/**
 * Formats a timestamp to a localized date and time string
 * @param timestamp - Date string or Date object
 * @returns Formatted datetime string like "Nov 28, 2024 3:30:45 PM" (localized)
 */
export function formatTimestampFull(timestamp: string | Date): string {
	return dayjs(timestamp).format('lll'); // Localized long format with date and time
}

/** Formats a timestamp relative to now, for example "3 hours ago". */
export function formatTimestampRelative(timestamp: string | Date): string {
	return dayjs(timestamp).fromNow();
}

/**
 * Formats a timestamp in date-first order so string sorting matches chronological sorting.
 * @param timestamp - Date string or Date object
 * @returns Formatted datetime string like "2026-07-23 21:12:00"
 */
export function formatTimestampSortable(timestamp: string | Date): string {
	return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
}
